import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useRef } from "react";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  staticSectionSx,
  staticContentSx,
  scrollContainerSx,
  stickyContainerSx,
  stickyContainerFullBleedSx,
  stickyInnerContentSx,
  contentWrapperSx,
  headerRowSx,
  leadTextSx,
  rowsContainerSx,
  rowsContainerNoInsetSx,
  advantageRowSx,
  rowLabelSx,
  highlightedLabelSx,
  rowDescriptionSx,
  highlightedDescriptionSx,
  rowTextContentSx,
  rowSecondaryDescriptionSx,
} from "./EnterpriseAdvantageSection.sx";
import {
  type AdvantageItem,
  sectionTitle,
  advantageItems,
} from "./EnterpriseAdvantageSection.config";
import { useEnterpriseAdvantageAnimation } from "./useEnterpriseAdvantageAnimation";

interface EnterpriseAdvantageSectionProps {
  sectionTitleText?: string;
  items?: AdvantageItem[];
  sectionId?: string;
  leadText?: string;
  enableScrollAnimation?: boolean;
  useRowsInset?: boolean;
  fullBleedBackground?: boolean;
  backgroundColor?: string;
  scrollContainerOverrideSx?: SxProps<Theme>;
  contentWrapperOverrideSx?: SxProps<Theme>;
}

export const EnterpriseAdvantageSection = ({
  sectionTitleText = sectionTitle,
  items = advantageItems,
  sectionId = "ai-strategy",
  leadText,
  enableScrollAnimation = true,
  useRowsInset = true,
  fullBleedBackground = false,
  backgroundColor = "#f8fafc",
  scrollContainerOverrideSx,
  contentWrapperOverrideSx,
}: EnterpriseAdvantageSectionProps = {}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const rowsRef = useRef<HTMLDivElement>(null);

  useEnterpriseAdvantageAnimation(sectionRef, rowsRef, enableScrollAnimation);

  const rowsSx = useRowsInset ? rowsContainerSx : rowsContainerNoInsetSx;
  const resolvedContentWrapperSx = [contentWrapperSx, contentWrapperOverrideSx];
  const resolvedScrollContainerSx =
    scrollContainerOverrideSx != null
      ? {
          ...(scrollContainerSx as Record<string, unknown>),
          ...(scrollContainerOverrideSx as Record<string, unknown>),
        }
      : scrollContainerSx;
  const resolvedStickyContainerSx = fullBleedBackground
    ? stickyContainerFullBleedSx
    : stickyContainerSx;

  const rows = (
    <Stack ref={rowsRef} sx={rowsSx}>
      {items.map((item) => {
        const isDescriptionHighlighted =
          item.isHighlighted && !item.highlightLabelOnly;
        return (
          <Box key={item.id} data-advantage-row sx={advantageRowSx}>
            <Typography
              sx={item.isHighlighted ? highlightedLabelSx : rowLabelSx}
            >
              {item.label}
            </Typography>
            <Box sx={rowTextContentSx}>
              <Typography
                sx={
                  isDescriptionHighlighted
                    ? highlightedDescriptionSx
                    : rowDescriptionSx
                }
                variant="body1"
              >
                {item.description}
              </Typography>
              {item.secondaryDescription != null && (
                <Typography sx={rowSecondaryDescriptionSx} variant="body1">
                  {item.secondaryDescription}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );

  if (!enableScrollAnimation) {
    return (
      <Box
        component="section"
        id={sectionId}
        sx={[staticSectionSx, { backgroundColor }] as SxProps<Theme>}
      >
        <Box sx={staticContentSx}>
          <Box sx={resolvedContentWrapperSx as SxProps<Theme>}>
            {leadText != null ? (
              <Box sx={headerRowSx}>
                <Typography variant="h2" component="h2">
                  {sectionTitleText}
                </Typography>
                <Typography sx={leadTextSx}>{leadText}</Typography>
              </Box>
            ) : (
              <Typography variant="h2" component="h2">
                {sectionTitleText}
              </Typography>
            )}
            {rows}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="section"
      ref={sectionRef}
      sx={resolvedScrollContainerSx}
      id={sectionId}
    >
      <Box
        sx={[resolvedStickyContainerSx, { backgroundColor }] as SxProps<Theme>}
      >
        <Box sx={fullBleedBackground ? stickyInnerContentSx : undefined}>
          <Box sx={resolvedContentWrapperSx as SxProps<Theme>}>
            {leadText != null ? (
              <Box sx={headerRowSx}>
                <Typography variant="h2" component="h2">
                  {sectionTitleText}
                </Typography>
                <Typography sx={leadTextSx}>{leadText}</Typography>
              </Box>
            ) : (
              <Typography variant="h2" component="h2">
                {sectionTitleText}
              </Typography>
            )}
            {rows}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EnterpriseAdvantageSection;
